import { usePersistentState } from '../../core/usePersistentState.ts';
import { AccountSelect } from '../../core/components/AccountSelect.tsx';
import { OwnerBadge, PageGeo } from '../../core/components/AssetBadges.tsx';
import { CountrySearch } from '../../core/components/CountrySearch.tsx';
import { api } from '../../core/apiClient.ts';
// modules/ad-assets/PagesTab.tsx
// Таблиця Fan Pages з фільтром по акаунту

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAssets } from './useAssets.ts';
import { PageDetailsModal } from './PageDetailsModal.tsx';
import { PublishPostModal } from './PublishPostModal.tsx';
import type { Page, PageGroup, SocialAccount } from '../../core/types.ts';

interface Props {
  socialAccounts: SocialAccount[];
}

function formatFanCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function PagesTab({ socialAccounts }: Props) {
  const { 
    pages, pagesLoading, pagesError, fetchPages, refreshPages, pagesRefreshing,
    pageDetails, pageDetailsLoading, fetchPageDetails, updatePageDetails, syncPageDetails,
    groups, groupsLoading, fetchGroups, createGroup, deleteGroup, assignGroup,
    syncPagePosts, syncingPagePosts,
  } = useAssets();
  
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [publishingPageId, setPublishingPageId] = useState<string | null>(null);
  const [postSyncStatus, setPostSyncStatus] = useState<Record<string, 'ok' | 'error'>>({});
  
  const [isManageGroupsOpen, setIsManageGroupsOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [socialAccountIds, setSocialAccountIds] = usePersistentState<string[]>('pages.accounts', []);
  const [selectedIds,setSelectedIds]=useState<string[]>([]),[bulkGeo,setBulkGeo]=useState(''),[bulkBusy,setBulkBusy]=useState(false),[bulkStatus,setBulkStatus]=useState('');
  const bulkFlight=useRef(false);
  const runBulk=async (mode:'geo'|'posts')=>{
    if(bulkFlight.current || !selectedIds.length || (mode==='geo'&&!bulkGeo))return;
    bulkFlight.current=true;setBulkBusy(true);
    const ids=[...selectedIds];let succeeded=0;const failures:string[]=[];
    try {
      for(let index=0;index<ids.length;index++) {
        const id=ids[index];setBulkStatus(`Виконується ${index+1}/${ids.length}`);
        try {
          if(mode==='geo')await api.put(`/api/pages/${encodeURIComponent(id)}/geo`,{geoCountry:bulkGeo});
          else {const result=await syncPagePosts(id);if(!result.ok)throw new Error(result.error || 'Не вдалося отримати пости');}
          succeeded++;
        }catch(error){failures.push(`${pages.find(page=>page.id===id)?.name || id}: ${error instanceof Error?error.message:String(error)}`);}
      }
      if(mode==='geo')await fetchPages();
      setBulkStatus(`Готово: ${succeeded}/${ids.length}.`+(failures.length?' Помилки: '+failures.join('; '):''));
    }finally{bulkFlight.current=false;setBulkBusy(false);}
  };

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const visiblePages = useMemo(() => {
    if (!socialAccountIds.length) return pages;
    const selected = new Set(socialAccountIds);
    return pages.filter((page) => (page.socialAccountIds ?? [page.socialAccountId]).some(id => selected.has(id)));
  }, [pages, socialAccountIds]);

  const handleRefresh = async () => {
    const ids = socialAccountIds.length ? socialAccountIds : socialAccounts.map((account) => account.id);
    for (const id of ids) await refreshPages(id);
    await fetchPages();
  };

  return (
    <div className="page-body" style={{ padding: 0 }}>
      <div style={{ marginBottom: 'var(--space-5)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-1)' }}>
            Fan Pages
            {pages.length > 0 && (
              <span className="text-subtle font-mono text-sm" style={{ marginLeft: 'var(--space-2)' }}>
                ({visiblePages.length}/{pages.length})
              </span>
            )}
          </h2>
          <AccountSelect accounts={socialAccounts} value={socialAccountIds} onChange={setSocialAccountIds} />
          {socialAccountIds.length > 0 && <button className="btn btn--secondary btn--sm" onClick={() => setSocialAccountIds([])}>Скинути фільтр</button>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            className="btn btn--secondary btn--sm"
            onClick={handleRefresh}
            disabled={pagesRefreshing || pagesLoading}
            title="Оновити тільки сторінки з Facebook"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: '0.9rem',
                animation: pagesRefreshing ? 'spin 1s linear infinite' : 'none',
              }}
            >
              ↻
            </span>
            {pagesRefreshing ? 'Оновлення…' : 'Оновити сторінки'}
          </button>
          <button 
            className="btn btn--outline" 
            onClick={() => setIsManageGroupsOpen(true)}
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
          >
            Управління групами
          </button>
        </div>
      </div>

      {pages.length>0 && <div className="glass-card" style={{display:'flex',alignItems:'center',gap:'var(--space-3)',flexWrap:'wrap',padding:'var(--space-3)',marginBottom:'var(--space-3)'}}>
        <label><input type="checkbox" aria-label="Вибрати всі видимі сторінки" disabled={bulkBusy || !visiblePages.length} checked={visiblePages.length>0 && visiblePages.every(page=>selectedIds.includes(page.id))}
          onChange={e=>setSelectedIds(e.target.checked?[...new Set([...selectedIds,...visiblePages.map(page=>page.id)])]:selectedIds.filter(id=>!visiblePages.some(page=>page.id===id)))}/> Вибрати всі ({visiblePages.length})</label>
        <span>Вибрано: {selectedIds.length}</span>
        <CountrySearch label="GEO для вибраних сторінок" value={bulkGeo} onChange={setBulkGeo} disabled={bulkBusy}/>
        <button className="btn btn--primary" disabled={bulkBusy || !selectedIds.length || !bulkGeo} onClick={()=>void runBulk('geo')}>Присвоїти GEO</button>
        <button className="btn btn--secondary" disabled={bulkBusy || !selectedIds.length} onClick={()=>void runBulk('posts')}>Отримати пости вибраних</button>
        <button className="btn btn--ghost" disabled={bulkBusy || !selectedIds.length} onClick={()=>setSelectedIds([])}>Зняти вибір</button>
      </div>}
      {bulkStatus && <p role="status" style={{overflowWrap:'anywhere'}}>{bulkStatus}</p>}
      {/* Loading skeleton */}
      {pagesLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 64, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Error */}
      {pagesError && !pagesLoading && (
        <div className="notification notification--error">{pagesError}</div>
      )}

      {/* Empty state */}
      {!pagesLoading && !pagesError && pages.length === 0 && (
        <div className="glass-card empty-state">
          <div className="empty-state__title">Немає сторінок</div>
          <div className="empty-state__desc">
            Синхронізуй акаунт для завантаження підключених Fan Pages.
          </div>
        </div>
      )}

      {/* Pages List */}
      {!pagesLoading && pages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visiblePages.map((page: Page) => (
            <div 
              key={page.id} 
              className="glass-card hover:bg-slate-800/50 transition-colors" 
              style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}
            >
              {/* Avatar */}
              <input type="checkbox" aria-label={'Вибрати сторінку '+(page.name || page.id)} checked={selectedIds.includes(page.id)} disabled={bulkBusy}
                onChange={e=>setSelectedIds(e.target.checked?[...selectedIds,page.id]:selectedIds.filter(id=>id!==page.id))}/>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: 'var(--color-surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {page.pictureUrl ? (
                  <img
                    src={page.pictureUrl}
                    alt={page.name ?? ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '1rem', color: 'var(--color-text-subtle)' }}>P</span>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setSelectedPageId(page.id)}>
                <div
                  className="font-medium"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: '2px',
                  }}
                  title={page.name ?? page.id}
                >
                  {page.name ?? page.id}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="font-mono text-xs text-subtle">{page.id}</span>
                  {(page.socialAccountIds ?? [page.socialAccountId]).map(id => <OwnerBadge key={id} id={id} accounts={socialAccounts}/>)}
                  <PageGeo page={page} disabled={bulkBusy} onSaved={() => { void fetchPages(); }}/>
                  {typeof page.isPublished === 'boolean' ? <span
                    className={`badge ${page.isPublished ? 'badge--active' : 'badge--disconnected'}`}
                    title={page.isPublished ? 'Meta: is_published=true — сторінка опублікована' : 'Meta: is_published=false — сторінка неопублікована. Це не підтверджує блокування Meta.'}
                  >{page.isPublished ? 'Live' : 'Suspended'}</span> : <span className="text-xs text-muted" title="Meta не повернула is_published. Оновіть сторінки, щоб перевірити статус.">Статус: —</span>}
                  <span className="text-xs text-subtle">
                    {formatFanCount(page.fanCount)} підписників
                  </span>
                  {page.category && (
                    <span className="badge badge--currency" style={{ fontSize: '0.65rem' }}>
                      {page.category}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexShrink: 0 }}>
                <select 
                  className="input" 
                  style={{ width: '120px', padding: '0.25rem 0.5rem', fontSize: '0.85rem', height: '30px' }}
                  value={page.groupId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    assignGroup(page.id, val);
                  }}
                >
                  <option value="">Без групи</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>

                {/* Sync Posts button */}
                <button
                  className={`btn ${
                    postSyncStatus[page.id] === 'ok'
                      ? 'btn--success'
                      : postSyncStatus[page.id] === 'error'
                      ? 'btn--danger'
                      : 'btn--outline'
                  }`}
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', height: '30px', whiteSpace: 'nowrap' }}
                  disabled={bulkBusy || syncingPagePosts[page.id]}
                  title="Завантажити останні пости цієї сторінки"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const res = await syncPagePosts(page.id);
                    setPostSyncStatus((prev) => ({
                      ...prev,
                      [page.id]: res.ok ? 'ok' : 'error',
                    }));
                    setTimeout(() => {
                      setPostSyncStatus((prev) => {
                        const next = { ...prev };
                        delete next[page.id];
                        return next;
                      });
                    }, 3000);
                  }}
                >
                  {syncingPagePosts[page.id]
                    ? '⟳'
                    : postSyncStatus[page.id] === 'ok'
                    ? '✓ Пости'
                    : postSyncStatus[page.id] === 'error'
                    ? '✗ Помилка'
                    : '↻ Пости'}
                </button>

                <button 
                  className="btn btn--primary" 
                  style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem', height: '30px' }}
                  onClick={(e) => { e.stopPropagation(); setPublishingPageId(page.id); }}
                >
                  Post
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manage Groups Modal (Inline implementation for simplicity) */}
      {isManageGroupsOpen && (
        <div className="modal-backdrop" onClick={() => setIsManageGroupsOpen(false)}>
          <div className="modal-content glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Управління групами</h3>
            
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
              <input 
                type="text" 
                className="input" 
                placeholder="Нова група (напр. UA)" 
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                style={{ flex: 1 }}
              />
              <button 
                className="btn btn--primary" 
                onClick={() => {
                  if (newGroupName.trim()) {
                    createGroup(newGroupName.trim());
                    setNewGroupName('');
                  }
                }}
              >
                Додати
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '300px', overflowY: 'auto' }}>
              {groupsLoading ? (
                <div className="text-subtle text-sm">Завантаження...</div>
              ) : groups.length === 0 ? (
                <div className="text-subtle text-sm">Груп поки немає</div>
              ) : (
                groups.map(g => (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-2)', background: 'var(--color-surface-1)', borderRadius: 'var(--radius-sm)' }}>
                    <span>{g.name}</span>
                    <button 
                      className="btn btn--danger" 
                      style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      onClick={() => {
                        if (confirm(`Видалити групу ${g.name}?`)) {
                          deleteGroup(g.id);
                        }
                      }}
                    >
                      Видалити
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn--outline" onClick={() => setIsManageGroupsOpen(false)}>
                Закрити
              </button>
            </div>
          </div>
        </div>
      )}

      <PageDetailsModal
        pageId={selectedPageId ?? ''}
        isOpen={!!selectedPageId}
        onClose={() => setSelectedPageId(null)}
        pageDetails={pageDetails}
        pageDetailsLoading={pageDetailsLoading}
        fetchPageDetails={fetchPageDetails}
        updatePageDetails={updatePageDetails}
        syncPageDetails={syncPageDetails}
      />

      {publishingPageId && (
        <PublishPostModal 
          pageId={publishingPageId} 
          pageName={pages.find(p => p.id === publishingPageId)?.name ?? ''}
          onClose={() => setPublishingPageId(null)} 
        />
      )}
    </div>
  );
}
